import {
  Duration,
  nanoSecToLocalTimestamp,
  nanoSecToSecStr,
  nanoSecToUTCTimestamp,
} from "@/shared/time";

/**
 * Enum representing the different modes for displaying timestamps in the visualization workspace.
 *
 * - Elapsed: Time relative to the start of the workspace (e.g., "0.000000000" at the beginning)
 * - Log: Raw epoch time in seconds.nanoseconds format
 * - Local: Human-readable local timestamp (e.g., "2024-01-15 10:30:45.123 AM PST")
 */
export enum TimeDisplayMode {
  Elapsed = "Elapsed (s)",
  ElapsedTime = "Elapsed (hh:mm:ss.nnn)",
  Log = "Log",
  Local = "Local",
  UTC = "UTC",
}

/**
 * Shared formatter for elapsed time display with nanosecond precision.
 * Note: Existential check can be removed when we upgrade to Node 23.
 */
export const elapsedTimeFormatter =
  "DurationFormat" in Intl
    ? new Intl.DurationFormat("en", {
        style: "digital",
        hours: "2-digit",
        fractionalDigits: 9,
      })
    : null;

export const TIME_DISPLAY_MODES = [
  TimeDisplayMode.Elapsed,
  TimeDisplayMode.ElapsedTime,
  TimeDisplayMode.Log,
  TimeDisplayMode.Local,
  TimeDisplayMode.UTC,
] as const;

export const DEFAULT_TIME_DISPLAY_MODE = TimeDisplayMode.Local;

/**
 * Formats a timestamp based on the current time display mode.
 *
 * @param time - The timestamp in nanoseconds
 * @param mode - The display mode to use
 * @param earliestTime - The earliest timestamp in the workspace (used for Elapsed mode)
 * @param includeTz - Whether to include the timezone in the output if local time is displayed (default: true)
 * @returns A formatted string representation of the timestamp
 */
export function formatTimeByMode(
  time: bigint,
  mode: TimeDisplayMode,
  earliestTime: bigint | null | undefined,
  includeTz: boolean = true,
): string {
  switch (mode) {
    case TimeDisplayMode.Elapsed:
      return nanoSecToSecStr(time - (earliestTime ?? 0n));
    case TimeDisplayMode.ElapsedTime: {
      // Format elapsed time as HH:MM:SS.nnnnnnnnn using Intl.DurationFormat
      if (!elapsedTimeFormatter) {
        return nanoSecToSecStr(time - (earliestTime ?? 0n));
      }
      const elapsedNanos = time - (earliestTime ?? 0n);
      const duration = Duration.from({ nanoseconds: elapsedNanos });
      return elapsedTimeFormatter.format(duration.toObject());
    }
    case TimeDisplayMode.Log:
      return nanoSecToSecStr(time);
    case TimeDisplayMode.Local: {
      const raw = nanoSecToLocalTimestamp(time);
      // raw is like "2025-12-02 12:04:08.247 PM PST"; the [0, 26] prefix
      // ("2025-12-02 12:04:08.247 PM ") is safe to slice because
      // dateToLocalTimestamp pins the locale to en-US 12-hour, making the
      // pre-timezone shape deterministic across browsers.
      return includeTz ? raw : raw.slice(0, 26);
    }
    case TimeDisplayMode.UTC:
      return nanoSecToUTCTimestamp(time);
  }
}
